import { axiosInstance } from './axiosInstance';

export type MigrationState = 'TODO' | 'DONE' | 'FAILED' | 'NOTRELEVANT' | 'AMBIGUOUS';
export type MigrationCategory = 'Group1' | 'Group2' | 'TvOnly' | 'Deletable';

export interface MigrationStateResponse {
  found: boolean;
  state?: MigrationState;
  category?: MigrationCategory;
  data?: {
    AccountID: string; // Backend uses AccountID (uppercase D)
    MigraSessionID: string; // Backend uses MigraSessionID (uppercase D)
    Email: string; // Backend uses Email, not EmailAddress!
    Category: MigrationCategory;
    State: MigrationState;
    RecommendedPhoneNumber?: number;
    PhoneNumber?: number;
    HasTv: boolean;
    HasActiveAssignment: boolean;
    CaId?: number;
    LastStepId?: string;
  };
}

export const migrationResource = {
  /**
   * Lekérdezi a migration state-et migraSessionId alapján
   */
  async getMigrationState(migraSessionId: string): Promise<MigrationStateResponse> {
    const res = await axiosInstance.get<MigrationStateResponse>(
      `/api/migration/state/${encodeURIComponent(migraSessionId)}`,
    );
    return res.data;
  },

  /**
   * Frissíti a migration state-et
   */
  async updateMigrationState(migraSessionId: string, state: MigrationState): Promise<{ success: boolean }> {
    const res = await axiosInstance.put<{ success: boolean }>(
      `/api/migration/state/${encodeURIComponent(migraSessionId)}?state=${state}`,
    );
    return res.data;
  },

  /**
   * Frissíti az utolsó lépést
   */
  async updateLastStep(migraSessionId: string, stepId: string): Promise<{ success: boolean }> {
    const res = await axiosInstance.put<{ success: boolean }>(
      `/api/migration/step/${encodeURIComponent(migraSessionId)}?stepId=${encodeURIComponent(stepId)}`,
    );
    return res.data;
  },

  /**
   * Frissíti a telefonszámot és a verifikáció dátumát
   */
  async updatePhoneNumber(migraSessionId: string, phoneNumber: string): Promise<{ success: boolean }> {
    const res = await axiosInstance.put<{ success: boolean }>(
      `/api/migration/phone/${encodeURIComponent(migraSessionId)}?phoneNumber=${encodeURIComponent(phoneNumber)}`,
    );
    return res.data;
  },

  /**
   * Beállítja az EmailVerificationDate-et az aktuális dátumra
   * (csak Group2-nél használandó, amikor a felhasználó megnyitja az email linket)
   */
  async updateEmailVerificationDate(migraSessionId: string): Promise<{ success: boolean }> {
    const res = await axiosInstance.put<{ success: boolean }>(
      `/api/migration/email-verification/${encodeURIComponent(migraSessionId)}`,
    );
    return res.data;
  },

  /**
   * Befejezi a migrációt
   */
  async finalizeMigration(migraSessionId: string, caId?: string): Promise<{ success: boolean }> {
    const url = `/api/migration/finalize/${encodeURIComponent(migraSessionId)}${caId ? `?caId=${encodeURIComponent(caId)}` : ''}`;
    const res = await axiosInstance.put<{ success: boolean }>(url);
    return res.data;
  },

  /**
   * Ellenőrzi hogy a telefonszám már migrált-e (Group2 DÁP flow)
   */
  async checkPhoneStatus(phoneNumber: string): Promise<{ isDone: boolean; migraSessionId: string | null }> {
    const res = await axiosInstance.get<{ isDone: boolean; migraSessionId: string | null }>(
      `/api/migration/check-phone?phoneNumber=${encodeURIComponent(phoneNumber)}`,
    );
    return res.data;
  },

  /**
   * Ellenőrzi hogy a CaId már migrált-e (Group2 DÁP flow)
   */
  async checkCaStatus(caId: string): Promise<{ isDone: boolean; migraSessionId: string | null }> {
    const res = await axiosInstance.get<{ isDone: boolean; migraSessionId: string | null }>(
      `/api/migration/check-ca?caId=${encodeURIComponent(caId)}`,
    );
    return res.data;
  },

  /**
   * Frissíti a nyereményjáték részvételi consent-et
   */
  async updateGameConsent(migraSessionId: string, consent: boolean): Promise<{ success: boolean }> {
    const res = await axiosInstance.put<{ success: boolean }>(
      `/api/migration/game-consent/${encodeURIComponent(migraSessionId)}?consent=${consent}`,
    );
    return res.data;
  },
};
